import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload as UploadIcon, CheckCircle } from 'lucide-react';
import './Upload.css';

function Upload() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    visibility: 'public',
    price: ''
  });
  const [file, setFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (500MB limit)
      if (selectedFile.size > 500 * 1024 * 1024) {
        setError('File size must be less than 500MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleThumbnailChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        setError('Thumbnail must be an image file');
        return;
      }
      setThumbnailFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a video file');
      return;
    }

    if (!thumbnailFile) {
      setError('Please select a thumbnail image');
      return;
    }

    if (!formData.title.trim()) {
      setError('Please enter a video title');
      return;
    }

    if (formData.visibility === 'private' && !formData.price.trim()) {
      setError('Please set a price for private videos');
      return;
    }

    const uploadData = new FormData();
    uploadData.append('video', file);
    uploadData.append('thumbnail', thumbnailFile);
    uploadData.append('title', formData.title);
    uploadData.append('description', formData.description);
    uploadData.append('visibility', formData.visibility);
    if (formData.visibility === 'private' && formData.price) {
      uploadData.append('price', formData.price);
    }

    try {
      setUploading(true);
      setError('');
      
      await axios.post('/api/videos/upload', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload video. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  if (uploadSuccess) {
    return (
      <div className="upload-success">
        <CheckCircle size={64} color="#00ff00" />
        <h2>Video Uploaded Successfully!</h2>
        <p>Redirecting to home page...</p>
      </div>
    );
  }

  return (
    <div className="upload-page">
      <div className="upload-container">
        <h1 className="upload-title">Upload Video</h1>
        
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-group">
            <label htmlFor="video-file" className="file-upload-label">
              <div className="file-upload-area">
                {file ? (
                  <>
                    <CheckCircle size={48} color="#00ff00" />
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <UploadIcon size={48} />
                    <p>Click to select video file</p>
                    <p className="file-hint">MP4, AVI, MOV, MKV, WEBM (Max 500MB)</p>
                  </>
                )}
              </div>
              <input
                type="file"
                id="video-file"
                accept="video/*"
                onChange={handleFileChange}
                className="file-input"
              />
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              className="input"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter video title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              className="input textarea"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Tell viewers about your video"
              rows="5"
            />
          </div>

          <div className="form-group">
            <label htmlFor="visibility">Visibility</label>
            <select
              id="visibility"
              name="visibility"
              className="input"
              value={formData.visibility}
              onChange={handleInputChange}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
          {formData.visibility === 'private' && (
            <div className="form-group">
              <label htmlFor="price">Price</label>
              <input
                type="number"
                id="price"
                name="price"
                className="input"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="Set a price for this private video"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="thumbnail-file">Thumbnail image *</label>
            <input
              type="file"
              id="thumbnail-file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="input"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/')}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={uploading || !file}
            >
              {uploading ? 'Uploading...' : 'Upload Video'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Upload;
